import React from "react";

import classNames from "classnames";
import { Controller } from "react-hook-form";
import NumberFormat from "react-number-format";

import { ErrorText, Label, HelperText } from "../app/Atoms/Form/FormCopy";
import { TextInput, TextAreaInput } from "../app/Atoms/Form/TextInput";
import { useJourney } from "./sign_up_journeys";

export const Button = ({
  primary,
  secondary,
  children,
  className,
  ...props
}: {
  primary?: boolean;
  secondary?: boolean;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <button
      className={classNames(className, "inline-block py-16 px-48 rounded", {
        "bg-posboss-pink text-white": primary || !(primary || secondary),
        "bg-blue-400 text-white": secondary,
      })}
      {...props}
    >
      {children}
    </button>
  );
};
export const Form = ({
  children,
  ...props
}: {
  children: React.ReactNode;
} & Partial<React.ComponentPropsWithoutRef<"form">>) => {
  return (
    <form
      {...props}
      className="space-y-48 flex flex-col items-center max-w-lg  mx-auto"
    >
      {children}
    </form>
  );
};
export const RadioInput = ({
  options,
  formProps,
  name,
  ...props
}: {
  options: { value: string; label: string }[];
  formProps: NotTypedYet;
  name: string;
} & Partial<React.ComponentPropsWithoutRef<"input">>) => {
  return (
    <div
      className={classNames(
        {
          "grid-cols-2": options.length == 2 || options.length == 4,
          "grid-cols-3": options.length == 3 || options.length >= 5,
        },
        "space-y-12 sm:space-y-0 sm:grid sm:gap-8 justify-evenly",
      )}
    >
      {options.map(({ value, label }) => (
        <div key={value}>
          <input
            {...props}
            type="radio"
            className="peer hidden appearance-none"
            name={name}
            id={`${name}[${value}]`}
            value={value}
            ref={formProps.register()}
          />
          <label
            htmlFor={`${name}[${value}]`}
            className="text-center w-full block hover:bg-blue-100 hover:border-blue-300
               peer-checked:shadow peer-checked:border-blue-700  peer-checked:bg-blue-400 peer-checked:text-white
             rounded px-8 py-16 border-2  border-neutral-300 cursor-pointer"
          >
            {label}
          </label>
        </div>
      ))}
    </div>
  );
};

export const InputField = ({
  label,
  helpText,
  icon,
  errorMessage,
  Input,
  ...props
}: {
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  icon?: React.ReactNode;
  errorMessage?: React.ReactNode;
  Input: React.ComponentType<NotTypedYet>;
  name: string;
  formProps: NotTypedYet;
}) => {
  return (
    <div className="w-full space-y-12">
      <div>
        {label && <Label htmlFor={props.name}>{label}</Label>}
        <HelperText>{helpText}</HelperText>
      </div>
      <div>
        <div className="relative">
          <Input {...props} />
        </div>
        <ErrorText inError={props.formProps.formState.errors[props.name]}>
          {errorMessage ||
            props.formProps.formState.errors[props.name]?.message}
        </ErrorText>
      </div>
    </div>
  );
};

export const RadioSelectField = ({
  ...props
}: {
  name: string;
  label: React.ReactNode;
  formProps: NotTypedYet;
  options: { value: string; label: string }[];
} & Partial<React.ComponentPropsWithoutRef<"input">>) => {
  return <InputField {...props} Input={RadioInput} />;
};
const SelectInput = ({
  label,
  value,
  formProps,
  name,
  checkBoxLabel,
  ...props
}: {
  label?: React.ReactNode;
  value?: string;
  formProps: NotTypedYet;
  name: string;
  checkBoxLabel?: React.ReactNode;
  errorMessage?: React.ReactNode;
}) => (
  <div className="flex space-x-4 items-center">
    <input
      type="checkbox"
      className="w-16 h-16 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
      name={name}
      id={name}
      value={value}
      ref={formProps.register()}
      {...props}
    />
    <label
      htmlFor={name}
      className="w-full peer-checked:border-blue-600 peer-checked:shadow  cursor-pointer"
    >
      {checkBoxLabel}
    </label>
  </div>
);
const PriceInput = ({
  formProps,
  className,
  name,
  ...props
}: {
  formProps: NotTypedYet;
  className?: string;
  name: string;
}) => {
  const { journey } = useJourney();
  const errors = null;
  const currencySign =
    {
      nz: "$",
      au: "$",
      uk: "£",
    }[journey.countryCode || "nz"] || "$";
  return (
    <div className="relative">
      <Controller
        control={formProps.control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <NumberFormat
            fullWidth
            thousandSeparator={true}
            decimalScale={2}
            onValueChange={(target) => {
              onChange(target.floatValue);
            }}
            value={value}
            isNumericString
            prefix={currencySign}
            className={classNames(
              className,
              "bg-white text-right focus:outline-none focus:ring border-2 border-neutral-200 rounded py-8 px-16 block w-full appearance-none leading-normal",
              { "border-red-500": formProps.errors[name] },
            )}
            {...props}
          />
        )}
      />
    </div>
  );
};
export const PriceField = ({
  ...props
}: {
  name: string;
  label: React.ReactNode;
  formProps: NotTypedYet;
}) => {
  return <InputField {...props} Input={PriceInput} />;
};
export const CheckBoxField = ({
  ...props
}: {
  name: string;
  label: React.ReactNode;
  formProps: NotTypedYet;
  checkedBoxLabel: React.ReactNode;
  helpText?: React.ReactNode;
}) => {
  return <InputField {...props} Input={SelectInput} />;
};

export const TextField = ({
  ...props
}: {
  name: string;
  label: React.ReactNode;
  formProps: NotTypedYet;
  helpText?: React.ReactNode;
  icon?: React.ReactNode;
  errorMessage?: React.ReactNode;
}) => {
  return <InputField {...props} Input={TextInput} />;
};

export const TextArea = ({
  ...props
}: {
  name: string;
  label: React.ReactNode;
  formProps: NotTypedYet;
  helpText?: React.ReactNode;
  icon?: React.ReactNode;
  errorMessage?: React.ReactNode;
}) => {
  return <InputField {...props} Input={TextAreaInput} />;
};
